import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  User,
  ShoppingCart,
  Users,
  Tags,
  Settings,
  LogOut,
  Truck,
  DollarSign,
  Gift,
  MapPin,
  Ticket,
  Calendar,
  Image,
  MessageSquare,
  FileText,
  CreditCard,
  Bell,
  Trash2,
  ClipboardCheck,
  Receipt,
  Landmark,
  BarChart3,
  ChevronRight,
  ShieldCheck,
  UserCheck,
  Store,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

interface SidebarProps {
  isMinimized: boolean;
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: any;
  label: string;
  path: string;
  id: string;
  badge?: string;
}

interface MenuGroup {
  title?: string;
  items: MenuItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({ isMinimized, isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const basePath = user?.role === 'vendor' ? '/vendor' : '/admin';

  const getMenuGroups = (): MenuGroup[] => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return [
        {
          title: 'GENEL',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', id: 'dashboard' },
            { icon: BarChart3, label: 'Ziyaretçi Analizi', path: '/admin/analytics', id: 'site-analytics' },
          ],
        },
        {
          title: 'KATALOG & İÇERİK',
          items: [
            { icon: Package, label: 'Ürünler', path: '/admin/products', id: 'products' },
            { icon: Gift, label: 'Ekstra Ürünler', path: '/admin/extra-products', id: 'extra-products' },
            { icon: Tags, label: 'Kategoriler', path: '/admin/taxonomy', id: 'taxonomy' },
            { icon: Ticket, label: 'Kuponlar', path: '/admin/coupons', id: 'coupons' },
            { icon: Calendar, label: 'Özel Tarihler', path: '/admin/special-dates', id: 'special-dates' },
            { icon: Image, label: 'Bannerlar', path: '/admin/banners', id: 'banners' },
            { icon: MessageSquare, label: 'Yorumlar', path: '/admin/reviews', id: 'reviews' },
            { icon: FileText, label: 'CMS Sayfaları', path: '/admin/pages', id: 'pages' },
          ],
        },
        {
          title: 'OPERASYON & LOJİSTİK',
          items: [
            { icon: ShoppingCart, label: 'Siparişler', path: '/admin/orders', id: 'orders' },
            { icon: MessageSquare, label: 'Canlı Destek', path: '/admin/live-chat', id: 'live-chat' },
            { icon: FileText, label: 'Fatura Kuyruğu', path: '/admin/orders/invoices', id: 'invoice-queue' },
            { icon: Trash2, label: 'Toplu Sipariş Sil', path: '/admin/orders/bulk-delete', id: 'bulk-delete' },
            { icon: Users, label: 'Müşteriler', path: '/admin/customers', id: 'customers' },
            { icon: Store, label: 'Vendor Yönetimi', path: '/admin/vendors', id: 'vendors' },
            { icon: Truck, label: 'Kuryeler', path: '/admin/couriers', id: 'couriers' },
            { icon: MapPin, label: 'Teslimat Bölgeleri', path: '/admin/delivery-areas', id: 'delivery-areas' },
            { icon: Bell, label: 'Bildirimler', path: '/admin/notifications', id: 'notifications' },
          ],
        },
        {
          title: 'FİNANS & POS',
          items: [
            { icon: DollarSign, label: 'Muhasebe', path: '/admin/accounting', id: 'accounting' },
            { icon: ClipboardCheck, label: 'Mutabakatlar', path: '/admin/settlements', id: 'settlements' },
            { icon: Receipt, label: 'Platform Giderleri', path: '/admin/platform-expenses', id: 'platform-expenses' },
            { icon: CreditCard, label: 'POS Yönetimi', path: '/admin/payment-providers', id: 'payment-providers' },
            { icon: Landmark, label: 'Banka Hesapları', path: '/admin/bank-accounts', id: 'bank-accounts' },
          ],
        },
        {
          title: 'SİSTEM',
          items: [
            { icon: UserCheck, label: 'Admin Hesapları', path: '/admin/admin-users', id: 'admin-users' },
            { icon: Settings, label: 'Site Ayarları', path: '/admin/settings', id: 'settings' },
            { icon: ShieldCheck, label: 'RBAC Yönetimi', path: '/admin/rbac-management', id: 'rbac-management' },
          ],
        },
      ];
    }

    if (user?.role === 'vendor') {
      const vendorItems: MenuItem[] = [
        { icon: ShoppingCart, label: 'Siparişler', path: '/vendor/orders', id: 'orders' },
        { icon: Bell, label: 'Bildirimler', path: '/vendor/notifications', id: 'notifications' },
        { icon: Truck, label: 'Kuryeler', path: '/vendor/couriers', id: 'couriers' },
        { icon: DollarSign, label: 'Muhasebe', path: '/vendor/accounting', id: 'accounting' },
        { icon: ClipboardCheck, label: 'Mutabakatlar', path: '/vendor/settlements', id: 'settlements' },
        { icon: User, label: 'Profilim', path: '/vendor/profile', id: 'profile' },
      ];

      const filtered = vendorItems.filter(
        (item) =>
          item.id === 'notifications' ||
          item.id === 'accounting' ||
          item.id === 'settlements' ||
          user?.menuItems?.includes(item.id)
      );

      return [{ title: 'PANELİM', items: filtered }];
    }

    // Default permission-based menu
    const allItems: MenuItem[] = [
      { icon: LayoutDashboard, label: 'Dashboard', path: `${basePath}`, id: 'dashboard' },
      { icon: Package, label: 'Ürünler', path: `${basePath}/products`, id: 'products' },
      { icon: Gift, label: 'Ekstra Ürünler', path: `${basePath}/extra-products`, id: 'extra-products' },
      { icon: ShoppingCart, label: 'Siparişler', path: `${basePath}/orders`, id: 'orders' },
      { icon: Users, label: 'Müşteriler', path: `${basePath}/customers`, id: 'customers' },
      { icon: Store, label: 'Vendor Yönetimi', path: `${basePath}/vendors`, id: 'vendors' },
      { icon: Truck, label: 'Kuryeler', path: `${basePath}/couriers`, id: 'couriers' },
      { icon: MapPin, label: 'Teslimat Bölgeleri', path: `${basePath}/delivery-areas`, id: 'delivery-areas' },
      { icon: DollarSign, label: 'Muhasebe', path: `${basePath}/accounting`, id: 'accounting' },
      { icon: Tags, label: 'Kategoriler', path: `${basePath}/taxonomy`, id: 'taxonomy' },
      { icon: Ticket, label: 'Kuponlar', path: `${basePath}/coupons`, id: 'coupons' },
      { icon: Calendar, label: 'Özel Tarihler', path: `${basePath}/special-dates`, id: 'special-dates' },
      { icon: Image, label: 'Bannerlar', path: `${basePath}/banners`, id: 'banners' },
      { icon: MessageSquare, label: 'Yorumlar', path: `${basePath}/reviews`, id: 'reviews' },
      { icon: FileText, label: 'Sayfalar', path: `${basePath}/pages`, id: 'pages' },
      { icon: CreditCard, label: 'POS Yönetimi', path: `${basePath}/payment-providers`, id: 'payment-providers' },
      { icon: Settings, label: 'Ayarlar', path: `${basePath}/settings`, id: 'settings' },
    ];

    const filtered = allItems.filter((item) => user?.menuItems?.includes(item.id));
    return [{ title: 'MENÜ', items: filtered }];
  };

  const menuGroups = getMenuGroups();

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/login');
  };

  const sidebarWidthClass = isMinimized ? 'lg:w-20' : 'lg:w-64';

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm transition-opacity lg:hidden"
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white text-slate-700 border-r border-slate-200/80 shadow-lg dark:bg-slate-900 dark:text-slate-300 dark:border-slate-800/80 transition-all duration-300 ease-in-out',
          isOpen ? 'translate-x-0 w-[280px]' : '-translate-x-full',
          'lg:static lg:z-0 lg:translate-x-0',
          sidebarWidthClass,
        ].join(' ')}
      >
        {/* Menu Navigation List */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {menuGroups.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              {group.title && !isMinimized && (
                <p className="px-3 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                  {group.title}
                </p>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    title={isMinimized ? item.label : undefined}
                    className={[
                      'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-150 min-h-[42px]',
                      isMinimized ? 'lg:justify-center lg:px-2' : 'justify-start',
                      active
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-500/25'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/70 dark:hover:text-white',
                    ].join(' ')}
                  >
                    <Icon
                      size={20}
                      className={[
                        'flex-shrink-0 transition-colors',
                        active ? 'text-white' : 'text-slate-400 group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-white',
                      ].join(' ')}
                    />
                    {!isMinimized && (
                      <span className="truncate flex-1">{item.label}</span>
                    )}
                    {!isMinimized && active && (
                      <ChevronRight size={14} className="text-white/70" />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User Footer Profile & Logout */}
        <div className="p-3 border-t border-slate-100 dark:border-slate-800/80 flex-shrink-0 bg-slate-50/60 dark:bg-slate-900/60">
          <button
            onClick={handleLogout}
            className={[
              'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10 dark:hover:text-rose-300 transition-colors min-h-[42px]',
              isMinimized ? 'lg:justify-center lg:px-2' : 'justify-start',
            ].join(' ')}
            title={isMinimized ? 'Çıkış Yap' : undefined}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!isMinimized && <span>Çıkış Yap</span>}
          </button>
        </div>
      </aside>
    </>
  );
};
